import { useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { formatDate } from "date-fns";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useGetCompanyBOV1 } from "@/api/company.api";
import { useGetOneExpeditionBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { PickupDateModal } from "@/components/shared/modal/pickupDate.modal";
import { RolesEnum } from "@/constants/enums/roles.enum";
import { userAtom } from "@/stores/authStore";
import {
  GetAllCompanyDto,
  GetOneExpeditionBoResponseDto,
  PickUpLocationFullCompanyDto,
} from "@/types/api/api-types";
import { CalendarEventType } from "@/types/bo/calendarType";
import { CotationsInfo } from "./selectModal/cotationsInfo";
import { CustomCotationsInfo } from "./selectModal/customCotationsInfo";
import { ZoneInfo } from "./selectModal/zoneInfo";
import { usePickupSlotShortLabel } from "@/hooks/useEnumLabels";

type CollectionSelectModalProps = {
  closeModal: () => void;
  event: CalendarEventType;
};

const POSTPONE_ROLES = new Set<RolesEnum>([
  RolesEnum.ADMIN,
  RolesEnum.SUPERADMIN,
  RolesEnum.DRIVER,
]);

const canUserPostpone = (role: string | undefined): boolean =>
  !!role && POSTPONE_ROLES.has(role as RolesEnum);

export const CollectionSelectModal = ({ closeModal, event }: CollectionSelectModalProps) => {
  const { t } = useTranslation("boLogistics");
  const getPickupSlotShortLabel = usePickupSlotShortLabel();
  const { data: company, isLoading: isLoadingCompany } = useGetCompanyBOV1(event.companyId);
  const { data: expedition, isLoading: isLoadingExpedition } = useGetOneExpeditionBo(
    event.expeditionId,
  );

  const user = useAtomValue(userAtom);
  const canPostpone = canUserPostpone(user?.role);

  const slotLabel = getPickupSlotShortLabel(event.pickupInfo?.pickupSlot);
  const pickupLocation = useMemo(
    () =>
      company?.pickUpLocations?.find(
        (location) => location.id === event.pickupInfo?.pickupLocation?.id,
      ) ?? null,
    [company?.pickUpLocations, event.pickupInfo?.pickupLocation?.id],
  );

  const [postponeOpen, setPostponeOpen] = useState(false);

  const isLoading = isLoadingCompany || isLoadingExpedition;
  const hasData = !!company && !!expedition;

  return (
    <>
      <Modal
        open
        onClose={closeModal}
        title={t("pickupDetailsModal.title")}
        dismissible
        footer={
          hasData && (
            <div className="flex w-full flex-row justify-center">
              <Button
                variant="outline"
                disabled={!canPostpone}
                onClick={() => setPostponeOpen(true)}
              >
                {t("pickupDetailsModal.postpone")}
              </Button>
            </div>
          )
        }
      >
        <Body
          isLoading={isLoading}
          company={company}
          expedition={expedition}
          event={event}
          pickupLocation={pickupLocation}
          slotLabel={slotLabel}
        />
      </Modal>

      {hasData && (
        <PickupDateModal
          open={postponeOpen}
          onClose={() => setPostponeOpen(false)}
          onSuccess={() => {
            setPostponeOpen(false);
            closeModal();
          }}
          expedition={expedition}
        />
      )}
    </>
  );
};

type BodyProps = {
  isLoading: boolean;
  company: GetAllCompanyDto | undefined;
  expedition: GetOneExpeditionBoResponseDto | undefined;
  event: CalendarEventType;
  pickupLocation: PickUpLocationFullCompanyDto | null;
  slotLabel: string;
};

const Body = ({ isLoading, company, expedition, event, pickupLocation, slotLabel }: BodyProps) => {
  const { t } = useTranslation("boLogistics");

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!company || !expedition) {
    return (
      <span className="text-center text-sm font-medium text-gray-900">
        {t("pickupDetailsModal.loadError")}
      </span>
    );
  }

  return (
    <div className="flex max-h-[80vh] flex-col gap-4 overflow-y-auto">
      <span className="text-center text-sm font-medium text-gray-900">
        {t("pickupDetailsModal.scheduledPickup", {
          date: formatDate(event.start, "dd/MM/yyyy"),
          slot: slotLabel,
        })}
      </span>
      <ZoneInfo pickupLocation={pickupLocation} />
      <CotationsInfo
        expedition={expedition}
        billingEntities={company.billingEntities}
        pickupLocation={pickupLocation}
      />
      {expedition.customQuotation && <CustomCotationsInfo expedition={expedition} />}
    </div>
  );
};
